import { useState, useEffect, useRef } from 'react';
import { api } from './hrApi';
import { useAuth } from './hrAuth';

const ROLE_LABELS = {
  ADMIN:            'Admin',
  RE:               'Resident Engineer',
  TEAM_LEAD:        'Team Lead',
  BHEL_COORDINATOR: 'BHEL Coordinator',
  BHEL_SPOC:        'BHEL SPOC',
  MANAGER:          'Manager',
  BHEL_MANAGEMENT:  'BHEL Management',
  SUPPLY_CHAIN:     'Supply Chain',
};

const FEAT_LABELS = {
  checkin:     'Check In / Out',
  attendance:  'Attendance',
  leaves:      'Leaves',
  history:     'History',
  direct_call: 'Direct Call',
  team:        'Team',
};

export default function Admin() {
  const [tab, setTab]         = useState('users');
  const [users, setUsers]     = useState([]);
  const [filter, setFilter]   = useState('');
  const [perms, setPerms]     = useState(null);
  const [feats, setFeats]     = useState([]);
  const [roles, setRoles]     = useState([]);
  const [permMsg, setPermMsg] = useState('');
  const prefixRef = useRef(null);

  const loadUsers = () => api('/admin/users').then(d => setUsers(d.users)).catch(() => {});
  const loadPerms = () => api('/admin/permissions').then(d => {
    setPerms(d.perms); setFeats(d.features); setRoles(d.roles);
  }).catch(() => {});

  useEffect(() => { loadUsers(); loadPerms(); }, []);

  const setRole = async (emp_id, role) => {
    try { await api(`/admin/users/${emp_id}`, { method: 'PATCH', body: JSON.stringify({ role }) }); }
    catch (e) { alert(e.message); loadUsers(); }
  };

  const savePerms = async () => {
    perms.re_prefix = prefixRef.current?.value || 'R';
    try {
      await api('/admin/permissions/save', { method: 'PUT', body: JSON.stringify({ perms }) });
      setPermMsg('Saved ✓');
    } catch (e) { setPermMsg(e.message); }
  };

  const filtered      = users.filter(u => !filter ||
    `${u.employee_id} ${u.name} ${u.email} ${u.role}`.toLowerCase().includes(filter.toLowerCase()));
  const activeCount   = users.filter(u => u.is_active).length;
  const enrolledCount = users.filter(u => u.face_enrolled).length;

  return (
    <section className="page on">
      <h2 className="pagetitle display">HR Admin</h2>
      <div className="pagesub">Users, roles, and feature access.</div>

      <div className="adm-topbar">
        <div className="adm-tabs">
          <button className={`adm-tab ${tab === 'users' ? 'sel' : ''}`} onClick={() => setTab('users')}>
            👥 Users <span className="adm-badge">{activeCount}</span>
          </button>
          <button className={`adm-tab ${tab === 'perms' ? 'sel' : ''}`} onClick={() => setTab('perms')}>
            🔐 Role access
          </button>
        </div>
      </div>

      {tab === 'users' ? (
        <>
          <div className="adm-stats">
            <div className="adm-stat"><span className="adm-stat-n">{users.length}</span><span className="adm-stat-l">Total</span></div>
            <div className="adm-stat"><span className="adm-stat-n" style={{color:'var(--ok)'}}>{activeCount}</span><span className="adm-stat-l">Active</span></div>
            <div className="adm-stat"><span className="adm-stat-n" style={{color:'var(--accent)'}}>{enrolledCount}</span><span className="adm-stat-l">Face enrolled</span></div>
            <div className="adm-stat"><span className="adm-stat-n" style={{color:'var(--out)'}}>{activeCount - enrolledCount}</span><span className="adm-stat-l">Missing face</span></div>
          </div>

          <div className="adm-toolbar">
            <input className="adm-search" placeholder="Search name, email, role…"
              value={filter} onChange={e => setFilter(e.target.value)} />
            <span className="note" style={{marginLeft:8}}>
              Add users from <strong>Settings → User Management</strong>
            </span>
          </div>

          <div className="adm-table-wrap">
            <table className="adm-table">
              <thead><tr>
                <th>EMP ID</th><th>NAME</th><th>EMAIL</th><th>ROLE</th>
                <th style={{textAlign:'center'}}>STATUS</th>
                <th style={{textAlign:'center'}}>FACE</th>
              </tr></thead>
              <tbody>{filtered.map(u => (
                <tr key={u.employee_id}>
                  <td className="adm-empid">{u.employee_id || '—'}</td>
                  <td>{u.name || '—'}</td>
                  <td className="adm-email">{u.email || '—'}</td>
                  <td>
                    <select className="adm-role-sel" value={u.role}
                      onChange={e => setRole(u.employee_id, e.target.value)}>
                      {Object.entries(ROLE_LABELS).map(([k, v]) => (
                        <option key={k} value={k}>{v}</option>
                      ))}
                    </select>
                  </td>
                  <td style={{textAlign:'center'}}>
                    <span className={`pill ${u.is_active ? 'ok' : 'bad'}`}>
                      {u.is_active ? 'Active' : 'Disabled'}
                    </span>
                  </td>
                  <td style={{textAlign:'center'}}>
                    <span className={`pill ${u.face_enrolled ? 'ok' : 'mut'}`}>
                      {u.face_enrolled ? '✓' : '—'}
                    </span>
                  </td>
                </tr>
              ))}</tbody>
            </table>
          </div>
          <div className="note" style={{marginTop:8}}>
            Showing {filtered.length} of {users.length} employees
          </div>
        </>
      ) : perms ? (
        <div className="card">
          <table className="t permgrid">
            <thead>
              <tr>
                <th>FEATURE</th>
                {roles.map(r => <th key={r}>{ROLE_LABELS[r] || r}</th>)}
              </tr>
            </thead>
            <tbody>{feats.map(f => (
              <tr key={f}>
                <td>{FEAT_LABELS[f] || f}</td>
                {roles.map(r => (
                  <td key={r}>
                    <input type="checkbox"
                      disabled={r === 'ADMIN'}
                      checked={perms.roles[r]?.[f] ?? false}
                      onChange={e => {
                        const np = {...perms};
                        np.roles[r][f] = e.target.checked;
                        setPerms({...np});
                      }} />
                  </td>
                ))}
              </tr>
            ))}</tbody>
          </table>
          <div className="savebar">
            <button className="primary" onClick={savePerms}>Save changes</button>
            <span className="note">{permMsg}</span>
          </div>
        </div>
      ) : <div className="loading">Loading…</div>}
    </section>
  );
}
